import { FormDataController } from "../formController";
import { useNavigate } from "react-router-dom";
import { Text } from "../../types/text";
import useTextQuery from "../../hooks/query/useTextQuery";
import { extractReferenceKey } from "../../components/forms/fields/basic/EntitySelectionField";
import useTextLogic from "../../hooks/logic/textLogic";

const useTextController = (
  id: string | null,
  systemLanguageType: Text["system_language_type"]
): FormDataController<Text> => {
  const { useFetchById, useCreate, useDeleteById, useUpdate } =
    useTextQuery(systemLanguageType);
  const { userEnums, metadataFieldNames, defaultText, getDisplayName } =
    useTextLogic(systemLanguageType);
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const deleteById = useDeleteById();
  const navigate = useNavigate();

  const fetchData = async (): Promise<Text> => {
    if (!id) {
      return defaultText;
    }
    const res = await fetchById.refetch();
    const textData = res.data;
    if (!textData || res.isError) {
      throw new Error("Text data not found");
    }
    return textData;
  };

  const onSubmit = async (data: Partial<Text>) => {
    const updateData: Partial<Text> = {
      based_on_type: data.based_on_type,
      digitization: data.digitization,
      name: data.name,
      //  size: data.size,
      sources: data.sources,
      text: data.text,
      version_id: extractReferenceKey(data.version),
      disabled: data.disabled,
      notes: data.notes,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      internal_notes: data.internal_notes,
      attributes_metadata: {
        ...defaultText.attributes_metadata,
        ...data.attributes_metadata,
      },
    };

    if (isCreateMode) {
      updateData.system_language_type = systemLanguageType;
    }

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...updateData },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Text");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create Text");
      }
    }
    return result;
  };

  const isCreateMode = id === null;

  const title = (data: Text) =>
    `${isCreateMode ? "Create New" : "Edit"} ${getDisplayName(data)}`;

  const onDelete = async () => {
    if (!id) {
      throw new Error("Text ID is required for deletion");
    }
    await deleteById.mutateAsync(id.toString());
    if (deleteById.isError) {
      throw new Error("Failed to delete text");
    }
  };

  const deleteWarningMessage = "Are you sure you want to delete this text?";

  const navigateBack = () => navigate(-1);

  return {
    defaultValues: defaultText,
    metadataFieldNames,
    userEnums,
    isCreateMode,
    title,
    fetchData,
    onSubmit,
    onDelete,
    deleteWarningMessage,
    formCallback: navigateBack,
  };
};

export default useTextController;

